import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSelectorCreator, defaultMemoize } from 'reselect';
import * as api from '../objectRights/objectRightsAPI';
import * as usersSlice from '../users/usersSlice';
import { closeModal } from '../modal/modalSlice';
import {setUserGroupSelection} from '../userGroups/userGroupsSlice';

const initialState = {
  status: 'idle',
  deleteObjectRightStatuses: {},
  deleteObjectRightStatusesByObjectRightId: {},
  listStatus: 'idle',
  participantsManageModalObjectId: null,
  inviteStatusById: {}, // Use separate statuses to prevent unwanted rerendering everywhere
  objectRightsById: {}, // We may have many objectRights visible on the screen at the same time (floor plan, document)
  objectRightsFetchedTs: {},  // We have probably fetched objectRights recently, check ts before new fetch
  invalidSearchValue: false, // I don't know why we need this
  hasPossibleViewPriceIncrease: false,
  hasPossibleEditPriceIncrease: false,
  hasPriceIncrease: false,
  notifyMessage: "",
  sendNotifyMessage: true,
  objectRightIdsByObjectId: {},
  sendPdfLink: false,

  userGroupRightsByObjectIdAndByGroupId: {},
  groupRightAddStatus: 'idle',
  groupRightRemoveStatus: {}
};

export const fetchObjectRights = createAsyncThunk(
  'objectRights/fetchObjectRights',
  async (params) => {
    let response = await api.fetchObjectRights(params);
    response.body.objectId = params.objectId;
    return response.body;
  }
);

export const newBEFetchObjectRights = createAsyncThunk(
  'objectRights/newBEFetchObjectRights',
  async (params) => {
    let response = await api.newBEFetchObjectRights(params);
    return response.body;
  }
);

export const deleteObjectRight = createAsyncThunk(
  'objectRights/deleteObjectRight',
  async (params, { dispatch }) => {

    let response;

    if (params.newBe) {
      response = await api.newBEDeleteObjectRight(params);
    } else {
      response = await api.deleteObjectRight(params);
    }

    if (params.fetchUserObjects) {
      dispatch(usersSlice.getUserObjects(params.userId));
    }
    // We always need to fetch object rights due to a bug:
    // 1. Visit the shared object (e.g., a document named 'Testipohja').
    // 2. Remove the right from UserObjectsModal.
    // 3. Revisit the object. The right will still appear to be present.
    dispatch(newBEFetchObjectRights(params));

    if (params.userId === 'company') {
      // TODO: MikkoS
      //dispatch(objectsSlice.fetchObjectsChangedSinceLastFetch()); // Do this to update object.company_rights
    }
    return response.body;
  }
);

export const saveObjectRights = createAsyncThunk(
  'objectRights/saveObjectRights',
  async (params, { dispatch, getState }) => {
    if (getState().objectRights.sendNotifyMessage) {
      params.message = getState().objectRights.notifyMessage;
    }

    if (params.noMessage) {
      delete params.message;
    }

    let response = await api.newBESaveObjectRights(params);

    if (!params.noFetch) {
      dispatch(newBEFetchObjectRights({ objectId: params.objectId }));
      // TODO: MikkoS
      //dispatch(objectsSlice.fetchObjectsChangedSinceLastFetch());
      dispatch(usersSlice.fetchUsers());
    }

    response.body.objectId = params.objectId;

    return response.body;
  }
);

export const resendObjectRightInvite = createAsyncThunk(
  'objectRights/resendObjectRightsInvite',
  async (params, { dispatch }) => {
    let response = await api.resendObjectRightsInvite(params);
    dispatch(newBEFetchObjectRights({ objectId: params.objectId }));

    return response.body;
  }
);

export const addGroupToObject = createAsyncThunk(
  'userGroups/addGroupToObject',
  async (params, thunkAPI) => {
    const response = await api.addGroupToObject(params);

    if (!response.isOk) {
      throw new Error('Failed to add group to object');
    }

    if (!params.noFetch) {
      const result = await thunkAPI.dispatch(newBEFetchObjectRights(params));
      if (result.meta.requestStatus !== 'fulfilled') {
        throw new Error('Failed to fetch object rights');
      }
    } 

    thunkAPI.dispatch(closeModal("addUserGroupsModal"));
    thunkAPI.dispatch(setUserGroupSelection(null));
  
    return response.body;
  }
);

export const removeGroupFromObject = createAsyncThunk(
  'objectRights/removeGroupFromObject',
  async (params, { dispatch }) => {
    let response = await api.removeGroupFromObject(params);
    if (!params.noFetch) {
      dispatch(newBEFetchObjectRights({objectId: params.objectId}));
    }
    return response.body;
  }
);

export const objectRightsSlice = createSlice({
  name: 'objectRights',
  initialState,
  reducers: {
    setParticipantsManageModalObjectId: (state, action) => {
      state.participantsManageModalObjectId = action.payload;
    },
    addObjectRight: (state, action) => {
      if (!state.objectRightsById[action.payload.objectId]) {
        state.objectRightsById[action.payload.objectId] = [];
      }
      state.objectRightsById[action.payload.objectId].push(action.payload);
    },
    removeNewObjectRights: (state, action) => {
      if (state.objectRightsById[action.payload]) {
        state.objectRightsById[action.payload] = state.objectRightsById[action.payload].filter(o => !o.isNew);
      }
    },
    removeNewAndModifiedObjectRights: (state, action) => {
      if (state.objectRightsById[action.payload]) {
        state.objectRightsById[action.payload] = state.objectRightsById[action.payload].filter(o => !o.isNew && !o.isModified);
      }
    },
    changeObjectRightType: (state, action) => {
      state.objectRightsById[action.payload.objectId].forEach((u, i) => {
        if (u.email === action.payload.email) {
          // Copy old values to make cancel possible
          if (
            !state.objectRightsById[action.payload.objectId][i].isModified &&
            !state.objectRightsById[action.payload.objectId][i].isNew
          ) {
            state.objectRightsById[action.payload.objectId][i].valuesInDatabase = { ...state.objectRightsById[action.payload.objectId][i] };
            state.objectRightsById[action.payload.objectId][i].isModified = true;
          }
          state.objectRightsById[action.payload.objectId][i].type = action.payload.value;
        }
      });
    },
    changeObjectRightExpiresTs: (state, action) => {
      state.objectRightsById[action.payload.objectId].forEach((u, i) => {
        if (u.email === action.payload.email) {
          // Copy old values to make cancel possible
          if (
            !state.objectRightsById[action.payload.objectId][i].isModified &&
            !state.objectRightsById[action.payload.objectId][i].isNew
          ) {
            state.objectRightsById[action.payload.objectId][i].valuesInDatabase = { ...state.objectRightsById[action.payload.objectId][i] };
            state.objectRightsById[action.payload.objectId][i].isModified = true;
          }
          state.objectRightsById[action.payload.objectId][i].expires_ts = action.payload.value;
        }
      });
    },
    // NOTE: Langauge is not saved into objectRight, so we dont show it with existing objectRight rows
    changeObjectRightLanguage: (state, action) => {
      state.objectRightsById[action.payload.objectId].forEach((u, i) => {
        if (u.email === action.payload.email) {
          state.objectRightsById[action.payload.objectId][i].language = action.payload.value;
        }
      });
    },
    // I don't know what this is but I guess we need this?
    setInvalidSearchValue: (state, action) => {
      state.invalidSearchValue = action.payload;
    },
    setHasPossibleViewPriceIncrease: (state, action) => {
      state.hasPossibleViewPriceIncrease = action.payload;
    },
    setHasPossibleEditPriceIncrease: (state, action) => {
      state.hasPossibleEditPriceIncrease = action.payload;
    },
    setHasPriceIncrease: (state, action) => {
      state.hasPriceIncrease = action.payload;
    },
    deleteNewObjectRight: (state, action) => {
      state.objectRightsById[action.payload.objectId] = state.objectRightsById[action.payload.objectId].filter(o => o.email !== action.payload.email);
    },
    cancelObjectRightModify: (state, action) => {
      state.objectRightsById[action.payload.objectId].forEach((u, i) => {
        if (u.email === action.payload.email) {
          state.objectRightsById[action.payload.objectId][i] = { ...state.objectRightsById[action.payload.objectId][i].valuesInDatabase };
        }
      });
    },
    copySelectionsToAllObjectRightsUnderModification: (state, action) => {
      const rootParticipant = state.objectRightsById[action.payload.objectId].find(u => u.email === action.payload.email);

      if (rootParticipant) {
        state.objectRightsById[action.payload.objectId].forEach((u, i) => {
          if (u.isNew || u.isModified) {
            state.objectRightsById[action.payload.objectId][i].language = rootParticipant.language;
            state.objectRightsById[action.payload.objectId][i].expires_ts = rootParticipant.expires_ts;
            state.objectRightsById[action.payload.objectId][i].type = rootParticipant.type;
          }
        });
      }
    },
    setNotifyMessage: (state, action) => {
      state.notifyMessage = action.payload;
    },
    setSendNotifyMessage: (state, action) => {
      state.sendNotifyMessage = action.payload;
    },
    setSendPdfLink: (state, action) => {
      state.sendPdfLink = action.payload;
    },
    addNewUserGroupRight: (state, action) => {
      const {objectId, groupId} = action.payload;

      const objectGroups = state.userGroupRightsByObjectIdAndByGroupId?.[objectId];
      if (!objectGroups || objectGroups[groupId]) {
        return;
      }

      objectGroups[groupId] = {
        id: groupId,
        isNew: true,
        rightType: 'view',
        expiresTs: null,
        createdTs: new Date().getTime(),
        modifiedTs: new Date().getTime()
      };
    },
    setUserGroupRightType: (state, action) => {
      const {objectId, groupId, value} = action.payload;

      const objectGroups = state.userGroupRightsByObjectIdAndByGroupId?.[objectId];
      if (!objectGroups || !objectGroups[groupId]) {
        return;
      }

      const groupRight = objectGroups[groupId];
      if (
        !groupRight.isModified &&
        !groupRight.isNew
      ) {
        groupRight.valuesInDatabase = {...groupRight};
        groupRight.isModified = true;
      }
      groupRight.rightType = value;
      groupRight.modifiedTs = new Date().getTime();
    },
    setUserGroupExpiresTs: (state, action) => {
      const {objectId, groupId, value} = action.payload;

      const objectGroups = state.userGroupRightsByObjectIdAndByGroupId?.[objectId];
      if (!objectGroups || !objectGroups[groupId]) {
        return;
      }

      const groupRight = objectGroups[groupId];
      if (
        !groupRight.isModified &&
        !groupRight.isNew
      ) {
        groupRight.valuesInDatabase = {...groupRight};
        groupRight.isModified = true;
      }
      groupRight.expiresTs = value;
      groupRight.modifiedTs = new Date().getTime();
    },
    setModifiedGroupsStatusPending: (state, action) => {
      const objectGroups = state.userGroupRightsByObjectIdAndByGroupId?.[action?.payload];
      if (!objectGroups) {
        return;
      }

      for (const groupId in objectGroups) {
        const group = objectGroups[groupId];
        if (!group.isNew && !group.isModified) {
          continue;
        }
        group.status = 'pending';
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchObjectRights.pending, (state) => {
        state.listStatus = 'loading';
      })
      .addCase(fetchObjectRights.fulfilled, (state, action) => {
        state.listStatus = 'idle';
        state.objectRightsById[action.payload.objectId] = action.payload;
        state.objectRightIdsByObjectId[action.payload.objectId] = action.payload.map(or => or.id !== undefined ? or.id : 0);
        state.objectRightsFetchedTs[action.payload.objectId] = new Date().getTime();
      })
      .addCase(fetchObjectRights.rejected, (state) => {
        state.listStatus = 'error';
      })
      .addCase(newBEFetchObjectRights.pending, (state) => {
        state.listStatus = 'loading';
      })
      .addCase(newBEFetchObjectRights.fulfilled, (state, action) => {
        state.listStatus = 'idle';
        const objectId = action.meta.arg.objectId;
        const objectRights = [];
        const userGroups = {};
        
        action.payload.forEach(item => {
          if (item.group_ids) {
            item.group_ids.forEach(groupId => {
              let group = userGroups[groupId];
              if (!group) {
                group = { id: groupId, users: [] };
                userGroups[groupId] = group;
              }
              group.users.push(item);
            });
          } else {
            objectRights.push(item);
          }
        });

        // Iterate userGroups and mark if all users have the same right type and expiry timestamp
        for (const groupId in userGroups) {
          let group = userGroups[groupId];
          const {users} = group;
          if (users.length === 0) {
            continue;
          }
          const firstType = users[0].type;
          const allSameType = users.every(object => object.type === firstType);
          if (allSameType) {
            group.rightType = firstType;
          } else {
            group.rightType = undefined;
          }
          const firstExpiresTs = users[0].expires_ts;
          const allSame = users.every(object => object.expires_ts === firstExpiresTs);
          if (allSame) {
            group.expiresTs = firstExpiresTs;
          } else {
            group.expiresTs = undefined;
          }
        }

        if (!state.userGroupRightsByObjectIdAndByGroupId[objectId]) {
          state.userGroupRightsByObjectIdAndByGroupId[objectId] = {};
        }
        state.userGroupRightsByObjectIdAndByGroupId[objectId] = userGroups;

        state.objectRightsById[objectId] = objectRights;
        state.objectRightIdsByObjectId[objectId] = objectRights.map(or => or.id !== undefined ? or.id : 0);
        state.objectRightsFetchedTs[objectId] = Date.now();
      })      
      .addCase(newBEFetchObjectRights.rejected, (state) => {
        state.listStatus = 'error';
      })
      .addCase(deleteObjectRight.pending, (state, action) => {
        state.status = 'removing';

        if (action.meta.arg.objectId) {
          state.deleteObjectRightStatuses[action.meta.arg.objectId] = "removing";
        }

        if (action.meta.arg.objectRightId) {
          state.deleteObjectRightStatusesByObjectRightId[action.meta.arg.objectRightId] = "removing";
        }

        if (action.meta.arg.userId === 'company') {
          state.deleteObjectRightStatusesByObjectRightId[action.meta.arg.userId] = "removing";
        }
      })
      .addCase(deleteObjectRight.fulfilled, (state, action) => {
        state.status = 'idle';

        if (action.meta.arg.objectId) {
          state.deleteObjectRightStatuses[action.meta.arg.objectId] = "idle";
        }

        if (action.meta.arg.objectRightId) {
          state.deleteObjectRightStatusesByObjectRightId[action.meta.arg.objectRightId] = "idle";
        }

        if (action.meta.arg.userId === 'company') {
          state.deleteObjectRightStatusesByObjectRightId[action.meta.arg.userId] = "idle";
        }
      })
      .addCase(deleteObjectRight.rejected, (state, action) => {
        state.status = 'error';

        if (action.meta.arg.objectId) {
          state.deleteObjectRightStatuses[action.meta.arg.objectId] = "error";
        }

        if (action.meta.arg.objectRightId) {
          state.deleteObjectRightStatusesByObjectRightId[action.meta.arg.objectRightId] = "error";
        }

        if (action.meta.arg.userId === 'company') {
          state.deleteObjectRightStatusesByObjectRightId[action.meta.arg.userId] = "error";
        }
      })
      .addCase(saveObjectRights.pending, (state) => {
        state.status = 'adding';
      })
      .addCase(saveObjectRights.fulfilled, (state, action) => {
        state.status = 'idle';

        state.objectRightsById[action.payload.objectId]?.forEach(o => {
          o.isNew = false;
          o.isModified = false;
        });

        state.notifyMessage = '';
      })
      .addCase(saveObjectRights.rejected, (state) => {
        state.status = 'error';
      })
      .addCase(resendObjectRightInvite.pending, (state, action) => {
        state.inviteStatus = 'resending';
        state.inviteStatusById[action.meta.arg.objectRightId] = 'resending';
      })
      .addCase(resendObjectRightInvite.fulfilled, (state, action) => {
        state.inviteStatusById[action.payload.objectRightId] = 'idle';
      })
      .addCase(resendObjectRightInvite.rejected, (state, action) => {
        state.inviteStatusById[action.meta.arg.objectRightId] = 'error';
      })
      .addCase(addGroupToObject.pending, (state) => {
        state.groupRightAddStatus = 'adding';
      })
      .addCase(addGroupToObject.fulfilled, (state, action) => {
        state.groupRightAddStatus = 'idle';

        // Reset modified rights
        const objectId = action?.meta?.arg?.objectId;
        const groupId = action?.meta?.arg?.groupId;
        const group = state.userGroupRightsByObjectIdAndByGroupId?.[objectId]?.[groupId];
        if (group) {
          delete group.isNew;
          delete group.isModified;
          delete group.createdTs;
          delete group.modifiedTs;
          delete group.valuesInDatabase;
        }
      })
      .addCase(addGroupToObject.rejected, (state) => {
        state.groupRightAddStatus = 'idle';
      })
      .addCase(removeGroupFromObject.pending, (state, action) => {
        const groupId = action.meta.arg.groupId;
        state.groupRightRemoveStatus[groupId] = true;
      })
      .addCase(removeGroupFromObject.fulfilled, (state, action) => {
        const objectId = action?.meta?.arg?.objectId;
        const groupId = action?.meta?.arg?.groupId;

        // Reset removing status
        delete state.groupRightRemoveStatus[groupId];

        // Remove the group from the list
        if (state.userGroupRightsByObjectIdAndByGroupId?.[objectId]?.[groupId]) {
          delete state.userGroupRightsByObjectIdAndByGroupId[objectId][groupId];
        }
      })
      .addCase(removeGroupFromObject.rejected, (state, action) => {
        const groupId = action.meta.arg.groupId;
        delete state.groupRightRemoveStatus[groupId];
      })
  },
});

export const {
  setParticipantsManageModalObjectId,
  addObjectRight,
  deleteNewObjectRight,
  cancelObjectRightModify,
  removeNewObjectRights,
  removeNewAndModifiedObjectRights,
  changeObjectRightType,
  changeObjectRightLanguage,
  changeObjectRightSetExpiresTs,
  changeObjectRightExpiresTs,
  setInvalidSearchValue,
  setHasPossibleViewPriceIncrease,
  setHasPossibleEditPriceIncrease,
  setHasPriceIncrease,
  copySelectionsToAllObjectRightsUnderModification,
  setNotifyMessage,
  setSendNotifyMessage,
  setSendPdfLink,
  addCompanyRightsToObjectRights,
  addNewUserGroupRight,
  setUserGroupRightType,
  setUserGroupExpiresTs,
  setModifiedGroupsStatusPending,
} = objectRightsSlice.actions;

export const selectObjectRightsById = (state, objectId) => state.objectRights.objectRightsById[objectId];
export const selectStatus = (state) => {
  const objectId = state.objectRights.participantsManageModalObjectId;
  const groupRights = state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[objectId] || {};

  for (const groupId in groupRights) {
    if (groupRights[groupId].status) {
      return groupRights[groupId].status;
    }
  }

  return state.objectRights.status;
}
export const selectDeleteObjectRightStatusById = (state, objectId) => {
  return state.objectRights.deleteObjectRightStatuses?.[objectId] ?? null;
}
export const selectDeleteObjectRightStatusByObjectRightId = (state, objectRightId) => {
  return state.objectRights.deleteObjectRightStatusesByObjectRightId?.[objectRightId] ?? null;
}
export const selectListStatus = (state) => state.objectRights.listStatus;
export const selectInviteStatusById = (state, objectRightId) => state.objectRights.inviteStatusById[objectRightId];
export const selectInviteStatus = (state) => state.objectRights.inviteStatus;
export const selectNewOrModifiedObjectRights = (state) => {
  return state.objectRights.objectRightsById[state.objectRights.participantsManageModalObjectId] ?
    state.objectRights.objectRightsById[state.objectRights.participantsManageModalObjectId].filter(o => o.isNew || o.isModified) :
    [];
};
export const selectNewOrModifiedGroupRights = (state) => {
  const objectId = state.objectRights.participantsManageModalObjectId;
  const groupRights = state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[objectId] || {};

  let newOrModifiedGroupRights = [];

  for (const groupId in groupRights) {
    if (groupRights[groupId].isNew || groupRights[groupId].isModified) {
      newOrModifiedGroupRights.push(groupRights[groupId]);
    }
  }

  return newOrModifiedGroupRights;
};
export const selectIsAnyObjectRightsModified = (state) => {
  return state.objectRights.objectRightsById[state.objectRights.participantsManageModalObjectId]?.some(o => o.isModified);
}

export const selectObjectRightsFetchedTs = (state, objectId) => state.objectRights.objectRightsFetchedTs[objectId] || null;
export const selectInvalidSearchValue = (state) => state.objectRights.invalidSearchValue;
export const selectParticipantsManageModalObjectId = (state) => state.objectRights.participantsManageModalObjectId;
export const selectNotifyMessage = (state) => state.objectRights.notifyMessage;
export const selectSendNotifyMessage = (state) => state.objectRights.sendNotifyMessage;
export const selectObjectRightIdsByObjectId = (state, objectId) => state.objectRights.objectRightIdsByObjectId[objectId] ?? {};
export const selectObjectRight = (state,objectId, index) => {
  return state.objectRights.objectRightsById[objectId][index] ?? null 
}
export const selectSendPdfLink = (state) => state.objectRights.sendPdfLink;

export const selectGroupRightType = (state, groupId) => {
  const groupRight = state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[state.objectRights.participantsManageModalObjectId]?.[groupId];

  if (!groupRight) {
    return null;
  }

  return groupRight.rightType;
};

export const selectGroupExpiresTs = (state, groupId) => {
  const groupRight = state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[state.objectRights.participantsManageModalObjectId]?.[groupId];

  if (!groupRight) {
    return null;
  }

  return groupRight.expiresTs; // null = expiry not set, undefined = multiple values
};

export const selectIsRightTypeModifiedWithoutCommonExpiresTs = (state, groupId) => {
  const groupRight = state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[state.objectRights.participantsManageModalObjectId]?.[groupId]
  if (!groupRight) {
    return false;
  }

  return (groupRight.isNew || groupRight.isModified) &&
    groupRight.rightType !== undefined &&
    groupRight.expiresTs === undefined; // null = expiry not set, undefined = multiple values
}

export const selectIsExpiresTsModifiedWithoutCommonRightType = (state, groupId) => {
  const groupRight = state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[state.objectRights.participantsManageModalObjectId]?.[groupId]
  if (!groupRight) {
    return false;
  }

  return (groupRight.isNew || groupRight.isModified) &&
    groupRight.expiresTs !== undefined &&
    groupRight.rightType === undefined;
}

export const selectGroupChangesArePossibleToBeSaved = (state) => {
  let possibleToBeSaved = true;
  const groupRights = state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[state.objectRights.participantsManageModalObjectId];

  for (const groupId in groupRights) {
    const groupRight = groupRights[groupId];
    if (!groupRight.isNew && !groupRight.isModified) {
      continue;
    }
        
    // Expires_ts can't be set if right type is 'multiple'
    if (
      groupRight.expiresTs !== undefined &&
      groupRight.rightType === undefined
    ) {
      possibleToBeSaved = false;
    }

    // Right type can't be set if expires_ts is 'multiple'
    if (
      groupRight.rightType !== undefined &&
      groupRight.expiresTs === undefined
    ) {
      possibleToBeSaved = false;
    }
  }

  return possibleToBeSaved;
}

export const selectObjectUserGroups = (state) => {
  return Object.values(state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[state.objectRights.participantsManageModalObjectId] ?? {});
}

export const selectUserGroupById = (state, groupId) => {
  return state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[state.objectRights.participantsManageModalObjectId]?.[groupId];
}

export const selectUserGroupsByObjectId = (state, objectId) => {
  return Object.values(state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[objectId] ?? {});
}

export const selectGroupRightRemoveStatus = (state, groupId) => {
  return state.objectRights.groupRightRemoveStatus[groupId]; // TODO: add object level
}

export const selectGroupRightSaveStatus = (state, groupId) => {
  const groupRight = state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[state.objectRights.participantsManageModalObjectId]?.[groupId];
  return groupRight?.status;
}

export const selectIsAnyUserGroupRightsModified = (state) => {
  const groupRights = state.objectRights.userGroupRightsByObjectIdAndByGroupId?.[state.objectRights.participantsManageModalObjectId] || {};

  for (const groupId in groupRights) {
    if (groupRights[groupId].isModified) {
      return true;
    }
  }

  return false;
}

export const selectIsAnyRightsModifiedOrNew = (state) => {
  return selectNewOrModifiedObjectRights(state).length || selectNewOrModifiedGroupRights(state).length;
}

const nameCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

const sameNamesAndMembership = (prevRights, nextRights) => {
  if (prevRights === nextRights) return true;
  if (!Array.isArray(prevRights) || !Array.isArray(nextRights) || prevRights.length !== nextRights.length) return false;
  return prevRights.every((prev, i) => {
    const next = nextRights[i];
    return prev === next || (
      prev?.id === next?.id &&
      prev?.last_name === next?.last_name &&
      prev?.first_name === next?.first_name &&
      prev?.isNew === next?.isNew
    );
  });
};

// Memoizes only the sort order (IDs); re-sorts only when names or membership change.
// The outer selector always resolves fresh objects so permission/expiration edits are never stale.
const createNameOrderSelector = createSelectorCreator(defaultMemoize, sameNamesAndMembership);

const selectSortedObjectRightIdsByObjectId = createNameOrderSelector(
  (state, objectId) => state.objectRights.objectRightsById[objectId] || [],
  (objectRights) =>
    [...objectRights]
      .filter(or => !or.isNew)
      .sort((a, b) => {
        const aHasName = !!(a.last_name || a.first_name);
        const bHasName = !!(b.last_name || b.first_name);
        return (
          (b.type === 'owner') - (a.type === 'owner') ||
          bHasName - aHasName ||
          nameCollator.compare(a.last_name || '', b.last_name || '') ||
          nameCollator.compare(a.first_name || '', b.first_name || '') ||
          nameCollator.compare(a.email || '', b.email || '')
        );
      })
      .map(or => or.id)
);

export const selectSortedObjectRightsByObjectId = (state, objectId) => {
  const objectRights = state.objectRights.objectRightsById[objectId] || [];
  const byId = new Map(objectRights.map(or => [or.id, or]));
  return selectSortedObjectRightIdsByObjectId(state, objectId)
    .map(id => byId.get(id))
    .filter(Boolean);
};

export default objectRightsSlice.reducer;
